import React, { useEffect, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { fetchDefaultTicketVisibilityForQueue } from 'flow/actions/tickets-action';
import Divider from 'flow/components/ui-components/Divider';
import RouteLink from 'flow/components/ui-components/RouteLink';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectHasTicketsSetAccessLevel } from 'flow/selectors/feature-flag-selectors';
import { selectDefaultTicketVisibilityForQueue } from 'flow/selectors/TicketsSelectors';
import { QueueKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { getTicketVisibilityGrantI18Key } from 'flow/utils/ticket-utils';

interface Props {
    queueEntityKey: QueueKey;
    disabled: boolean;
}

/**
 * The way into the queue's default non-member access, gated on the same flag as
 * the per-ticket control.
 *
 * Default visibility is a separate resource in the ticket service, not part of
 * the queue ticket settings, so it is fetched on its own — and only where the
 * row is shown, since the flag is per user.
 */
const PbxTicketsSettingsVisibilityRow = ({
    queueEntityKey,
    disabled,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const hasTicketsSetAccessLevel = useAppSelector(
        selectHasTicketsSetAccessLevel,
    );

    const defaultVisibility = useAppSelector((state) =>
        selectDefaultTicketVisibilityForQueue(state, queueEntityKey),
    );

    const [isFetchingVisibility, setIsFetchingVisibility] = useState(false);

    useEffect(() => {
        if (!hasTicketsSetAccessLevel) return;

        setIsFetchingVisibility(true);
        dispatch(fetchDefaultTicketVisibilityForQueue(queueEntityKey)).finally(
            () => {
                setIsFetchingVisibility(false);
            },
        );
    }, [dispatch, queueEntityKey, hasTicketsSetAccessLevel]);

    if (!hasTicketsSetAccessLevel) return null;

    return (
        <>
            <Divider />
            <RouteLink
                to={generatePath(
                    routes['pbx.queues.entityKey.settings.tickets.visibility'],
                    {
                        entityKey: queueEntityKey,
                    },
                )}
                description={
                    isFetchingVisibility
                        ? undefined
                        : i18next.t(
                              getTicketVisibilityGrantI18Key(
                                  defaultVisibility,
                              ) || 'status.unknown',
                          )
                }
                disabled={disabled}
            >
                {i18next.t('ticket.settings.queue.default.non.member.access')}
            </RouteLink>
        </>
    );
};

export default PbxTicketsSettingsVisibilityRow;
